/**
 * EventBus.ts — typed publish/subscribe for discrete domain events.
 *
 * Systems emit events (e.g. ore unlocked, building bought, achievement earned);
 * the render/UI layer subscribes. Systems never touch the DOM directly, which
 * keeps them pure and headless-testable.
 */

export interface GameEvents {
  'ore:unlocked': { oreId: string };
  'layer:unlocked': { depth: number };
  'building:bought': { buildingId: string; count: number };
  'upgrade:bought': { upgradeId: string };
  'worker:hired': { workerId: string; count: number };
  'tech:researched': { techId: string };
  'prestige': { points: number; total: number };
  'achievement:earned': { achievementId: string };
  'quest:completed': { questId: string; reward: string };
  'quest:offered': { questId: string };
  'event:started': { eventId: string; name: string; description: string };
  'event:ended': { eventId: string };
  'modifier:added': { id: string; label: string };
  'ability:activated': { abilityId: string; name: string };
  'nugget:spawned': { kind: string };
  'nugget:claimed': { kind: string; reward: string };
  'nugget:expired': {};
  'ascend': { cores: number; total: number };
  'challenge:started': { id: string };
  'challenge:completed': { id: string };
  'energy:deficit': { deficit: number };
  'energy:restored': {};
  'sell': { oreId: string; amount: number; value: number };
  'mine:click': { oreId: string; amount: number };
  'notify': { kind: 'info' | 'success' | 'warning' | 'reward'; message: string };
  'save': { manual: boolean };
  'load': { offlineMs: number };
  'reset': {};
}

type EventKey = keyof GameEvents;
type Handler<K extends EventKey> = (payload: GameEvents[K]) => void;

export class EventBus {
  private handlers = new Map<EventKey, Set<Handler<EventKey>>>();

  on<K extends EventKey>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<EventKey>);
    return () => this.off(event, handler);
  }

  off<K extends EventKey>(event: K, handler: Handler<K>): void {
    this.handlers.get(event)?.delete(handler as Handler<EventKey>);
  }

  emit<K extends EventKey>(event: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of [...set]) {
      (handler as Handler<K>)(payload);
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}
